import { FreezerService, managedAjaxUtil, IAjaxState } from '../Imports';
import { UserManagementService } from '../state/UserManagementFreezerService';
import {
    FleetSettingsApiFactory,
    FleetSettingResponse,
    VideoEventRetentionSettingsResponse,
    MessageResponse,
    WebsiteVideoEventRetentionRequest,
    AutoScoreSettingsResponse,
    AutoScoreSettingsRequest,
    FleetTimezoneResponse,
    FleetTimezoneRequest,
} from '../generated/api';

const InjectedPropName = 'FleetSettings';

interface IFleetSettingsState {
    getFleetSettingsByKeyResults: IAjaxState<FleetSettingResponse>;
    getVideoEventRetentionSettings: IAjaxState<VideoEventRetentionSettingsResponse>;
    saveVideoEventRetentionSettings: IAjaxState<MessageResponse>;
    getAutoScoreSettingsByFleetResults: IAjaxState<AutoScoreSettingsResponse[]>;
    saveAutoScoreSettings: IAjaxState<MessageResponse>;
    removeAutoScoreSetting: IAjaxState<MessageResponse>;
    getVideoOverlayTimezone: IAjaxState<FleetTimezoneResponse>;
    saveVideoOverlayTimezone: IAjaxState<MessageResponse>;
    hasError: boolean;
}

class FleetSettingsFreezerService extends FreezerService<IFleetSettingsState, typeof InjectedPropName> {
    constructor() {
        super(
            {
                getFleetSettingsByKeyResults: managedAjaxUtil.createInitialState(),
                getVideoEventRetentionSettings: managedAjaxUtil.createInitialState(),
                saveVideoEventRetentionSettings: managedAjaxUtil.createInitialState(),
                getAutoScoreSettingsByFleetResults: managedAjaxUtil.createInitialState(),
                saveAutoScoreSettings: managedAjaxUtil.createInitialState(),
                removeAutoScoreSetting: managedAjaxUtil.createInitialState(),
                getVideoOverlayTimezone: managedAjaxUtil.createInitialState(),
                saveVideoOverlayTimezone: managedAjaxUtil.createInitialState(),
                hasError: false,
            },
            InjectedPropName,
        );
    }

    public async getFleetSettingsByKeyResults(key: string): Promise<FleetSettingResponse | void> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'getFleetSettingsByKeyResults',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const fleetSettingsAPI = FleetSettingsApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return fleetSettingsAPI.apiV1FleetSettingsGet(params);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                key,
            },
            onOk: (data: FleetSettingResponse) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }

                return data;
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async getVideoEventRetentionSettings(): Promise<VideoEventRetentionSettingsResponse | void> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'getVideoEventRetentionSettings',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const fleetSettingsAPI = FleetSettingsApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return fleetSettingsAPI.apiV1FleetSettingsVideoEventRetentionGet(params);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
            },
            onOk: (data: VideoEventRetentionSettingsResponse) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }

                return data;
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async saveVideoEventRetentionSettings(retentionDays: number, keepEventData: boolean): Promise<MessageResponse | void> {
        const requestParam: WebsiteVideoEventRetentionRequest = {
            retentionDays: retentionDays,
            keepEventData: keepEventData,
        };
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'saveVideoEventRetentionSettings',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const fleetSettingsAPI = FleetSettingsApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return fleetSettingsAPI.apiV1FleetSettingsVideoEventRetentionPost(params);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                body: requestParam,
            },
            onOk: (data: MessageResponse) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }

                return data;
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async getAutoScoreSettingsByFleetResults(): Promise<void | AutoScoreSettingsResponse[]> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'getAutoScoreSettingsByFleetResults',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const fleetSettingsAPI = FleetSettingsApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return fleetSettingsAPI.apiV1FleetSettingsAutoScoreSettingsGet(params);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                ...{},
            },
            onOk: (data: AutoScoreSettingsResponse[]) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }

                return data;
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async saveAutoScoreSettings(id: number | undefined, eventTypeId: number, scoreId: number): Promise<MessageResponse | void> {
        const requestParam: AutoScoreSettingsRequest = {
            id: id,
            videoEventTypeId: eventTypeId,
            videoEventScoreId: scoreId,
        };
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'saveAutoScoreSettings',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const fleetSettingsAPI = FleetSettingsApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return fleetSettingsAPI.apiV1FleetSettingsAutoScoreSettingsPost(params);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                body: requestParam,
            },
            onOk: (data: MessageResponse) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }

                return data;
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async removeAutoScoreSetting(eventTypeId: number): Promise<MessageResponse | void> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'removeAutoScoreSetting',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const fleetSettingsAPI = FleetSettingsApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return fleetSettingsAPI.apiV1FleetSettingsAutoScoreSettingsDelete(params);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                videoEventTypeId: eventTypeId,
            },
            onOk: (data: MessageResponse) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }
                return data;
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async getVideoOverlayTimezone(): Promise<FleetTimezoneResponse | void> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'getVideoOverlayTimezone',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const fleetSettingsAPI = FleetSettingsApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return fleetSettingsAPI.apiV1FleetSettingsTimezoneGet(params);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
            },
            onOk: (data: FleetTimezoneResponse) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }
                return data;
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async saveVideoOverlayTimezone(timezone: string): Promise<MessageResponse | void> {
        const requestParam: FleetTimezoneRequest = { timezone };
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'saveVideoOverlayTimezone',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const fleetSettingsAPI = FleetSettingsApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return fleetSettingsAPI.apiV1FleetSettingsTimezonePost(params);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                body: requestParam,
            },
            onOk: (data: MessageResponse) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }
                return data;
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }
}

export const FleetSettingsService = new FleetSettingsFreezerService();
export type IFleetSettingsServiceInjectedProps = ReturnType<FleetSettingsFreezerService['getPropsForInjection']>;
